import { SOLVE_CANVAS_BOUNDS, SOLVE_PHASES, phaseIndex, } from "@playbookai/types";
/** Read valid geometry from either a JSON projection or a Yjs adapter. */
export function parseSolvePhaseAreaGeometry(value) {
    if (typeof value !== "object" || value === null || Array.isArray(value))
        return {};
    const result = {};
    for (const phase of SOLVE_PHASES) {
        const area = value[phase];
        if (typeof area !== "object" || area === null || Array.isArray(area))
            continue;
        const { x, w, h } = area;
        if (typeof x !== "number" ||
            !Number.isFinite(x) ||
            typeof w !== "number" ||
            !Number.isFinite(w) ||
            w <= 0 ||
            typeof h !== "number" ||
            !Number.isFinite(h) ||
            h <= 0)
            continue;
        result[phase] = { x, w, h };
    }
    return result;
}
/** Width of a phase area that has never been resized. */
export const SOLVE_LANE_WIDTH = SOLVE_CANVAS_BOUNDS.width / SOLVE_PHASES.length;
/** Inset between an area's left edge and its first widget. */
export const SOLVE_WIDGET_CONTENT_INSET_X = 32;
/**
 * Top of an area's content band — below the phase header card, which is 56px
 * tall with a 40px gap under it.
 */
export const SOLVE_WIDGET_CONTENT_TOP_Y = 96;
/** Clearance kept between a placed widget and anything already on the canvas. */
export const SOLVE_WIDGET_PLACEMENT_GAP = 40;
/**
 * Native default size per SOLVE canvas widget kind — the ONE size source for
 * both creators.
 *
 * These are the widget registry's own `defaultSize` values
 * (`packages/ui/src/widgets/<kind>/def.ts`), because that is what each widget
 * actually needs in order to render. `packages/ui`'s
 * `solve-widget-default-sizes.test.ts` fails if a registry def and this table
 * disagree, so the two cannot drift again.
 *
 * Exhaustive over `SOLVE_CANVAS_WIDGET_KINDS`, so a new renderable SOLVE kind
 * does not compile until it has a size here. The two legacy Oppress kinds are
 * included for that exhaustiveness even though neither creator can produce one.
 */
export const SOLVE_WIDGET_DEFAULT_SIZES = {
    problemStatement: { w: 352, h: 324 },
    tags: { w: 352, h: 444 },
    runChart: { w: 352, h: 324 },
    kpiTile: { w: 352, h: 324 },
    problemTree: { w: 752, h: 584 },
    problemTimeline: { w: 752, h: 384 },
    containmentPlan: { w: 752, h: 384 },
    exposureRiskReview: { w: 752, h: 384 },
    paretoL1: { w: 352, h: 444 },
    paretoL2: { w: 352, h: 444 },
    funnel: { w: 352, h: 444 },
    causalChain: { w: 352, h: 724 },
    fishbone: { w: 752, h: 324 },
    processTrace: { w: 752, h: 384 },
    impactEffort: { w: 752, h: 384 },
    countermeasureTable: { w: 752, h: 324 },
    stakeholderMap: { w: 752, h: 384 },
    controlPlan: { w: 352, h: 384 },
    escalationMatrix: { w: 352, h: 324 },
    genericTable: { w: 560, h: 320 },
    markdownText: { w: 560, h: 320 },
    currentStateStats: { w: 352, h: 300 },
    containmentCards: { w: 352, h: 300 },
};
/** The fixed lane a phase occupies when no area has been stored for it. */
export function defaultSolvePhaseAreaRect(phase) {
    return {
        x: phaseIndex(phase) * SOLVE_LANE_WIDTH,
        y: 0,
        w: SOLVE_LANE_WIDTH,
        h: SOLVE_CANVAS_BOUNDS.height,
    };
}
/**
 * Every area's rect, in phase order.
 *
 * A stored entry is authoritative for `w`/`h`. Its `x` is honoured unless it
 * would sit behind the previous area's right edge, in which case it is pushed
 * flush — so concurrent edits can never resolve to an overlap. With nothing
 * stored the `max` is an identity (each fixed lane starts exactly where its
 * predecessor ends), which is what keeps a canvas nobody has resized on its
 * historical geometry.
 *
 * The gap is deliberately NOT applied here: reading must not move an area a
 * writer never placed.
 */
export function resolveSolvePhaseAreaLayout(phases, stored) {
    const layout = new Map();
    let previousRight = null;
    for (const phase of phases) {
        const entry = stored[phase];
        const fallback = defaultSolvePhaseAreaRect(phase);
        const requestedX = entry ? entry.x : fallback.x;
        const rect = {
            x: previousRight === null
                ? requestedX
                : Math.max(requestedX, previousRight),
            y: 0,
            w: entry ? entry.w : fallback.w,
            h: entry ? entry.h : fallback.h,
        };
        layout.set(phase, rect);
        previousRight = rect.x + rect.w;
    }
    return layout;
}
/** The first widget slot inside an area. */
export function solvePhaseAreaContentOrigin(rect) {
    return {
        x: rect.x + SOLVE_WIDGET_CONTENT_INSET_X,
        y: SOLVE_WIDGET_CONTENT_TOP_Y,
    };
}
/**
 * Where a new widget goes: the phase area's content origin, then straight down
 * past anything in the way.
 *
 * The sweep is deterministic and terminates: each step moves below the lowest
 * blocker it found, clearing at least one, and never returns upward. It does not
 * clamp back onto occupied space when content already extends past the nominal
 * canvas height — a canvas can grow.
 */
export function getSolveWidgetPlacement(request) {
    const size = request.size ?? SOLVE_WIDGET_DEFAULT_SIZES[request.widgetType];
    const layout = resolveSolvePhaseAreaLayout(SOLVE_PHASES, request.phaseAreas ?? {});
    const origin = request.phase === "any"
        ? { x: parkingColumnX(layout), y: SOLVE_WIDGET_CONTENT_TOP_Y }
        : solvePhaseAreaContentOrigin(layout.get(request.phase) ?? defaultSolvePhaseAreaRect(request.phase));
    const occupied = request.existingWidgets ?? [];
    const { x } = origin;
    let { y } = origin;
    for (;;) {
        const blockers = occupied.filter((other) => overlaps({ x, y, w: size.w, h: size.h }, other));
        if (blockers.length === 0)
            return { x, y, w: size.w, h: size.h };
        y =
            Math.max(...blockers.map((other) => other.y + other.h)) +
                SOLVE_WIDGET_PLACEMENT_GAP;
    }
}
/**
 * Where a phase-agnostic widget parks: right of every area, and never left of
 * the nominal canvas width, so a board nobody has widened keeps the column it
 * has always used.
 */
function parkingColumnX(layout) {
    let right = SOLVE_CANVAS_BOUNDS.width;
    for (const rect of layout.values())
        right = Math.max(right, rect.x + rect.w);
    return right + SOLVE_WIDGET_CONTENT_INSET_X;
}
/** Axis-aligned overlap with the placement gap applied on all four sides. */
function overlaps(candidate, other) {
    const gap = SOLVE_WIDGET_PLACEMENT_GAP;
    return (candidate.x < other.x + other.w + gap &&
        candidate.x + candidate.w + gap > other.x &&
        candidate.y < other.y + other.h + gap &&
        candidate.y + candidate.h + gap > other.y);
}
export function planSolvePhaseAreaGrowth(phase, position, geometry, visiblePhases) {
    if (phase === "any")
        return null;
    const layout = resolveSolvePhaseAreaLayout(visiblePhases, geometry);
    const area = layout.get(phase);
    if (!area)
        return null;
    const w = Math.max(area.w, position.x + position.w - area.x + SOLVE_WIDGET_CONTENT_INSET_X);
    const h = Math.max(area.h, position.y + position.h + SOLVE_WIDGET_PLACEMENT_GAP);
    if (w === area.w && h === area.h)
        return null;
    const dx = w - area.w;
    const later = dx === 0 ? [] : visiblePhases.slice(visiblePhases.indexOf(phase) + 1);
    const rects = [
        [phase, { x: area.x, w, h }],
    ];
    for (const next of later) {
        const rect = layout.get(next);
        rects.push([next, { x: rect.x + dx, w: rect.w, h: rect.h }]);
    }
    return { rects, carry: { dx, phases: later, fromX: area.x + area.w } };
}
//# sourceMappingURL=solve-widget-placement.js.map